/* eslint-disable prettier/prettier */
import { apiService } from './api.service';
import {
	CourseSummary,
	CourseDetails,
	CourseFilters,
	CreateCourseRequest,
	EnrollmentSummary,
	AddCoachRequest,
	Solution,
} from '../types/course';

import axios from 'axios';
import { ApiResponse, ApiError } from '../types/index';

export interface UpdateCoursePayload {
	title?: string;
	description?: string;
	coursetype?: string;
	deliverymode?: string;
	duration?: number;
	difficulty?: string;
	category?: string;
	tags?: string[];
	price?: number;
	currency?: string;
	maxenrollments?: number;
	status?: string;
	ispublic?: boolean;
	solutionid?: string;
	programownerid?: string;
	programformat?: string;
	certification?: string;
	programincludes?: string[];
	learningtakeaways?: string[];
	metadata?: any;
	coverimageurl?: string;
	issuspended?: boolean;
}

export interface DuplicateCourseRequest {
	batchlabel?: string;
	startdate?: string | null;
	enddate?: string | null;
	status?: 'draft' | 'published';
	title?: string;
	programownerid?: string;
	maxenrollments?: number;

	solutionid?: string;
	copyAssessments?: boolean;
	copyResources?: boolean;
}

export interface CourseBatch {
	id: string;
	batchof: string;
	batchnumber: number;
	batchlabel?: string;
	startdate?: string | null;
	enddate?: string | null;
	status: string;
	isbatch: boolean;
	createdat: Date;
	enrollment_count?: number;
	faculty_count?: number;
	coach_count?: number;
}

// Response type
export interface CourseResponse {
	id: string;
	title: string;
	description: string;
}

class CourseService {
	// Toggle this to true to use API, false to use Mock
	private useMock = false;

	async getCoursesCatalog(filters?: CourseFilters) {
		try {
			if (this.useMock) {
				return;
			}
			const response = await apiService.get<{
				courses: CourseSummary[];
				total: number;
				page: number;
				limit: number;
			}>('course/catalog', filters);

			if (!response.success)
				throw new Error(response.message || 'Failed to fetch catalog');
			return response.data;
		} catch (error) {
			console.error('Error in getCoursesCatalog:', error);
			throw error;
		}
	}

	async getMyPrograms(page = 1, limit = 20) {
		try {
			const response = await apiService.get<{
				courses: CourseSummary[];
				total: number;
			}>('/course/coach/myprograms', { page, limit });

			if (!response.success)
				throw new Error(response.message || 'Failed to fetch catalog');
			return response.data;
		} catch (error) {
			console.error('Error in getCoursesByCoach:', error);
			throw error;
		}
	}

	async getCourseDetails(courseId: string, userId?: string) {
		try {
			const response = await apiService.get<CourseDetails>(
				`course/${courseId}`,
				{ userId }
			);
			if (!response.success)
				throw new Error(response.message || 'Failed to fetch details');
			console.log('Course Details Response:', response);
			console.log('Course Details Data:', response.data);
			return response.data;
		} catch (error) {
			console.error('Error in getCourseDetails:', error);
			throw error;
		}
	}

	//create course

	async createCourse(data: CreateCourseRequest) {
		try {
			const response = await apiService.post<any>('course', data);
			if (!response.success)
				throw new Error(response.message || 'Failed to create course');
			return response.data;
		} catch (error) {
			console.error('Error in createCourse:', error);
			throw error;
		}
	}

	/**
	 * Update Course
	 */
	async updateCourse(
		courseId: string,
		payload: UpdateCoursePayload
	): Promise<ApiResponse<CourseResponse>> {
		try {
			const response = await apiService.put<CourseResponse>(
				`/course/${courseId}`,
				payload
			);

			return response;
		} catch (error: any) {
			if (axios.isAxiosError(error)) {
				return {
					success: false,
					status: error.response?.status || 500, // ✅ FIX
					message:
						error.response?.data?.message ||
						error.response?.data?.error ||
						'Failed to update course',
					data: null as any,
				};
			}

			return {
				success: false,
				status: 500, // ✅ FIX
				message: error?.message || 'Something went wrong',
				data: null as any,
			};
		}
	}

	/**
	 * Duplicate Course (Create Batch)
	 */
	async duplicateCourse(
		courseId: string,
		payload: DuplicateCourseRequest
	): Promise<ApiResponse<any>> {
		return apiService.post<any>(`/course/${courseId}/duplicate`, payload);
	}

	/**
	 * Get Course Batches
	 */
	async getCourseBatches(
		courseId: string
	): Promise<ApiResponse<CourseBatch[]>> {
		return apiService.get<CourseBatch[]>(`/course/${courseId}/batches`);
	}
	// ---------- Cover ----------
	uploadCover(courseId: string, file: File) {
		const formData = new FormData();

		formData.append('cover', file);

		return apiService
			.post(`course/${courseId}/cover`, formData, {
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			})
			.then(res => {
				console.log('✅ Upload Success Response:', res);
				return res;
			})
			.catch(err => {
				console.error('❌ Upload Error:', err);

				if (err.response) {
					console.error('❌ Error Status:', err.response.status);
					console.error('❌ Error Data:', err.response.data);
				}

				throw err;
			});
	}

	// ---------- Coaches ----------
	addCoach(courseId: string, data: AddCoachRequest) {
		return apiService.post(`course/${courseId}/faculty`, data);
	}

	removeCoach(courseId: string, coachId: string) {
		console.log(
			'DELETE URL',
			`/courses/${courseId}/faculty/${coachId}`
		);
		return apiService.delete(`course/${courseId}/faculty/${coachId}`);
	}

	// ---------- Solutions ----------
	async getSolutions() {
		const res = await apiService.get<Solution[]>(`course/solutions`);
		if (!res.success) throw new Error(res.message);
		return res.data;
	}

	async createSolution(data: { title: string; description?: string }) {
		const res = await apiService.post<Solution>(`/course/solutions`, data);

		if (!res.success) throw new Error(res.message);

		return res.data; // ✅ IMPORTANT
	}

	async enrollInCourse(courseId: string) {
		try {
			const response = await apiService.post<void>(
				`course/${courseId}/enroll`,
				{}
			);
			if (!response.success)
				throw new Error(response.message || 'Enrollment failed');
			return response.data;
		} catch (error) {
			console.error('Error in enrollInCourse:', error);
			throw error;
		}
	}

	async getUserEnrollments(status?: string) {
		try {
			const response = await apiService.get<EnrollmentSummary[]>(
				'course/my-enrollments',
				{ status }
			);
			if (!response.success)
				throw new Error(
					response.message || 'Failed to fetch enrollments'
				);
			return response.data;
		} catch (error) {
			console.error('Error in getUserEnrollments:', error);
			throw error;
		}
	}

	async updateLessonProgress(
		enrollmentId: string,
		lessonId: string,
		data: { progress: number; completed: boolean; timespent?: number }
	) {
		try {
			const response = await apiService.post<void>(
				`course/progress/${enrollmentId}/${lessonId}`,
				data
			);
			if (!response.success)
				throw new Error(
					response.message || 'Failed to update progress'
				);
			return response.data;
		} catch (error) {
			console.error('Error in updateLessonProgress:', error);
			throw error;
		}
	}

	async getCourseDetailsWithResources(courseId: string, userId?: string) {
		try {
			const response = await apiService.get<CourseDetails>(
				'course/course/resource',
				{ courseId, userId },
				courseId
			);

			console.log('Course Details with Resources Response:', response);

			if (!response.success) {
				throw new Error(
					response.message || 'Failed to fetch course resources'
				);
			}
			console.log('Course Details with Resources Data:', response.data);
			return response.data;
		} catch (error) {
			console.error('Error in getCourseDetailsWithResources:', error);
			throw error;
		}
	}

	async getCourseWithModulesAndSessions(courseId: string) {
		try {
			const response = await apiService.get<any>(
				`course/${courseId}/modulesessions`
			);

			console.log('Course With Modules & Sessions Response:', response);

			if (!response.success) {
				throw new Error(
					response.message ||
					'Failed to fetch course modules & sessions'
				);
			}

			return response.data;
		} catch (error) {
			console.error('Error in getCourseWithModulesAndSessions:', error);
			throw error;
		}
	}
	async addFacultyToProgram(
		courseId: string,
		data: {
			userid: string;
			facultyrole?: string;
			displayorder?: number;
		}
	) {
		try {
			const response = await apiService.post<any>(
				`course/${courseId}/faculty`,
				data
			);

			if (!response.success) {
				throw new Error(response.message || 'Failed to add faculty');
			}

			return response.data;
		} catch (error) {
			console.error('Error in addFacultyToProgram:', error);
			throw error;
		}
	}
	async removeFacultyFromProgram(courseId: string, userId: string) {
		try {
			const response = await apiService.delete<any>(
				`course/${courseId}/faculty/${userId}`
			);

			if (!response.success) {
				throw new Error(response.message || 'Failed to remove faculty');
			}

			return response.data;
		} catch (error) {
			console.error('Error in removeFacultyFromProgram:', error);
			throw error;
		}
	}
	async removeProgramOwner(courseId: string) {
		try {
			const response = await apiService.delete(
				`course/${courseId}/owner`
			);

			if (!response.success) {
				throw new Error(
					response.message || 'Failed to remove program owner'
				);
			}

			return response.data;
		} catch (error) {
			console.error('Error in removeProgramOwner:', error);
			throw error;
		}
	}
	// ✅ Coach Profile Programs
	async getCoachProfilePrograms(coachId: string) {
		try {
			const response = await apiService.get<any>(
				`course/coach/${coachId}/programs`
			);

			if (!response.success) {
				throw new Error(
					response.message || 'Failed to fetch coach programs'
				);
			}

			return response.data;
		} catch (error) {
			console.error('Error in getCoachProfilePrograms:', error);
			throw error;
		}
	}

	// ✅ Learner Profile Programs
	async getLearnerProfilePrograms(learnerId: string) {
		try {
			const response = await apiService.get<any>(
				`course/learner/${learnerId}/programs`
			);

			if (!response.success) {
				throw new Error(
					response.message || 'Failed to fetch learner programs'
				);
			}

			return response.data;
		} catch (error) {
			console.error('Error in getLearnerProfilePrograms:', error);
			throw error;
		}
	}
}

export const courseService = new CourseService();
